import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'devextreme-react/button';
import { Item, Toolbar } from 'devextreme-react/toolbar';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth';
import { useTitle } from "../../../contexts/title";
import { useGetAnnouncements } from '../../../api/announcements';
import { useMarkAnnouncementAsRead } from '../../../features/common/announcements/api/mark-announcement-as-read';
import AnnouncementList from '../../../features/common/announcements/components/AnnouncementList';
import { showErrorMessage } from '../../../utils/messages';
import AnnouncementSearch from '../../../features/common/announcements/components/AnnouncementSearch';
import { LoadPagePanel } from '../../../components';
import { useMediaQuery } from '../../../utils/media-query';

export default function AnnouncementPage() {
	const navigate = useNavigate();

	const pageTitle = useTitle();
	useEffect(() => {
		pageTitle.setTitle('Announcement')
	}, [pageTitle]);

	const isNarrowScreen = useMediaQuery('(max-width: 658px)');

	const { user } = useAuth();
	const announcements = useGetAnnouncements(false, user?.userName!);

	const queryClient = useQueryClient();
	const markAnnouncementAsRead = useMarkAnnouncementAsRead(undefined,
		() => {
			queryClient.invalidateQueries(["announcements"]);
			queryClient.invalidateQueries(["unread-announcement-count"]);
		},
		(error) => {
			showErrorMessage(error);
		});

	const announcementContainerRef = useRef<HTMLDivElement>(null);

	return (
		<React.Fragment>
			<div className={'content-block'}>
				<Toolbar className={'button-toolbar ' + (announcements.data.length > 0 ? 'tall ' : 'short')} multiline={isNarrowScreen}>
					{
						!announcements.data.length &&
						<Item location='before'>
							<div>
								{announcements.isLoading ? "Loading announcements..." :
									"No new announcements at this time. Check back later for updates."}
							</div>
						</Item>
					}
					{
						announcements.data.length > 0 &&
						<Item location='before'>
							<AnnouncementSearch elementToSearch={announcementContainerRef.current!} />
						</Item>
					}
					<Item location='after'>
						<Button
							text="Past Announcements"
							icon="Inscyth-Announcement"
							onClick={() => navigate('/announcement/archive')}>
						</Button>
					</Item>
					{
						announcements.data.length > 0 &&
						<Item location='after'>
							<Button
								text="Mark All As Read"
								icon="Inscyth-Order-Material"
								disabled={markAnnouncementAsRead.isLoading}
								onClick={() => markAnnouncementAsRead.mutate()}>
							</Button>
						</Item>
					}
				</Toolbar>

				<div ref={announcementContainerRef}>
					<AnnouncementList announcements={announcements.data} />
				</div>

				<LoadPagePanel visible={announcements.isLoading || markAnnouncementAsRead.isLoading} />
			</div>
		</React.Fragment>
	)
} 
